import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import OpalHeading from '../../../../../opal/OpalHeading'
import OpalPresentationTable from '../../../../../organisms/DenaliAnalysis/tables/presentation'
import SydButton from '../../../../../commonDesign/Button'
import ModifyDatasourceAuthorizationDialog from '../../dialogs/ModifyDatasourceAuthorizationDialog'

function getPostureBadge (authorization) {
  if (!authorization) {
    return { label: 'Restricted (All-Accounts Only)', variant: 'error' }
  }
  if (authorization.mode === 'open') {
    return { label: 'Open Access', variant: 'success' }
  }
  if (authorization.mode === 'entity') {
    return { label: 'Entity-Scoped', variant: 'warning' }
  }
  return { label: 'Unknown', variant: 'default' }
}

function AuthorizationTab ({ datasource }) {
  const dialogRef = useRef(null)
  const authorization = datasource.data?.authorization || null

  const posture = getPostureBadge(authorization)

  const rulesData = useMemo(() => {
    if (authorization?.mode !== 'entity') return []
    return (authorization.rules || []).map(rule => {
      const dp = datasource.datapoints?.find(d => d.catalogDatapointId === rule.datapointId)
      return {
        groupTypeId: rule.groupTypeId,
        column: dp?.accessor || `(datapoint ${rule.datapointId})`
      }
    })
  }, [authorization, datasource.datapoints])

  if (datasource.type !== 'table') {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Card>Authorization is not supported for {datasource.type} datasources.</Card>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Card variant='clear'>
          <OpalHeading level={1} className='font-size-md flex gap-sm'>
            <span>Entity Authorization</span>
            <SydButton size='sm' variant='outline' icon='edit' onClick={() => dialogRef.current.open(datasource)}>Edit</SydButton>
          </OpalHeading>
          <p>
            Entity Authorization controls which rows a user can see when querying this datasource.
            It filters data based on the user&apos;s authorized entities (clients, accounts, etc.) via the user authorizations system.
          </p>
          <p>
            <strong>Current posture: </strong>
            <span className={`badge badge-${posture.variant}`}>{posture.label}</span>
          </p>
        </Card>
      </Grid>

      {authorization?.mode === 'entity' && rulesData.length > 0 && (
        <Grid item xs={12}>
          <OpalPresentationTable
            columns={[
              { accessorKey: 'groupTypeId', title: 'Group Type ID', id: 'groupTypeId' },
              { accessorKey: 'column', title: 'Column', id: 'column' }
            ]}
            data={rulesData}
            defaultSort={[]}
          />
        </Grid>
      )}

      <ModifyDatasourceAuthorizationDialog ref={dialogRef} />
    </Grid>
  )
}

AuthorizationTab.propTypes = {
  datasource: PropTypes.shape({
    type: PropTypes.string,
    data: PropTypes.object,
    datapoints: PropTypes.array,
    catalogDatasourceId: PropTypes.number
  })
}

export default AuthorizationTab
